import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { University } from '../../../models/university';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent, ColumnConfig } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Country } from '../../../models/country';
import { LutService } from '../../../services/lut.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-university-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    LookupTableSearchBoxComponent,
    NgxSpinnerModule
  ],
  templateUrl: './university-list.component.html',
  styleUrl: './university-list.component.scss'
})
export class UniversityListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  universities = signal<University[]>([]);
  countries: Country[] = [];
  isSearchVisible: boolean = false;
  isExporting: boolean = false;
  matchedItems: University[] | undefined = undefined;
  rightItem: University | undefined = undefined;

  // Pagination state
  currentPage: number = 1;
  pageSize: number = 25;
  totalCount: number = 0;
  sortBy: string = 'name';
  ascending: boolean = true;

  // Search filter state
  searchFilter: LookupTableSearchFilter = {};

  universitiesColumns: ColumnConfig[] = [];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private spinnerService: NgxSpinnerService,
    private lutService: LutService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadCountries();
    this.loadUniversities();
  }

  private loadCountries(): void {
    this.lutService.getCountries().subscribe({
      next: (countriesFlat) => {
        // Convert flat API response to hierarchical structure
        this.countries = Helpers.convertCountryFlatToCountry(countriesFlat).sort((a, b) => a.name.localeCompare(b.name));

        // Create select options for countries
        const countryOptions = this.countries.map(country => ({
          value: country.id,
          label: country.name
        }));

        this.universitiesColumns = [
          { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true },
          {
            key: 'countryId',
            header: 'Country',
            type: 'select',
            sortable: true,
            editable: true,
            selectOptions: countryOptions
          }
        ];
      },
      error: (error) => {
        console.error('Error loading countries:', error);
        this.toast.error(`<strong>Failed to load Countries:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  loadUniversities(): void {
    this.spinnerService.show();

    // Check if any search filters are active
    const hasSearchFilters = this.searchFilter.name || (this.searchFilter as any).countryId;

    if (hasSearchFilters) {
      // Use search endpoint when filters are active
      const searchRequest = {
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        sortBy: this.sortBy,
        ascending: this.ascending,
        ...this.searchFilter
      };

      this.lutService.searchUniversities(searchRequest).subscribe({
        next: (response) => {
          this.universities.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('Error loading universities:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Universities:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    } else {
      // Use regular paginated endpoint when no filters are active
      this.lutService.getUniversitiesPaginated(
        this.currentPage,
        this.pageSize,
        this.sortBy,
        this.ascending
      ).subscribe({
        next: (response) => {
          this.universities.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('Error loading universities:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Universities:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    }
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1; // MatPaginator uses 0-based index
    this.pageSize = event.pageSize;
    this.loadUniversities();
  }

  onSortChange(sort: Sort): void {
    // Map column keys to actual sortable fields
    const sortColumnMap: { [key: string]: string } = {
      'countryId': 'countryName'
    };

    // If sort direction is cleared (empty string), reset to default sort
    if (!sort.direction) {
      this.sortBy = 'name';
      this.ascending = true;
    } else {
      this.sortBy = sortColumnMap[sort.active] || sort.active;
      this.ascending = sort.direction === 'asc';
    }

    this.loadUniversities();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  onExportToCsv(): void {
    if (this.isExporting) return;
    this.isExporting = true;
    this.spinnerService.show();

    this.lutService.exportUniversitiesToCsv({
      name: this.searchFilter.name,
      countryId: (this.searchFilter as any).countryId
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `universities_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.spinnerService.hide();
        this.isExporting = false;
        this.toast.success('Universities exported successfully');
      },
      error: (error) => {
        console.error('Error exporting universities:', error);
        this.toast.error(`<strong>Failed to export Universities:</strong></br></br> ${Helpers.parseApiError(error)}`);
        this.spinnerService.hide();
        this.isExporting = false;
      }
    });
  }

  getAppliedFiltersCount(): number {
    const { exactMatch, ...actualFilters } = this.searchFilter;
    return Object.values(actualFilters).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.searchFilter = searchFilter;
    this.currentPage = 1; // Reset to first page when searching
    this.loadUniversities();
  }

  onCreateUniversity(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(university: University): void {
  }

  onReadyToMatch(event: {matchedItems?: University[], rightItem?: University}): void {
    this.matchedItems = event.matchedItems;
    this.rightItem = event.rightItem;
  }

  onUniversityCorrected(): void {
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        const originalUniversities = this.universities();

        this.universities.update(universities =>
          universities.filter(u => !bulkMergeRequest.leftIds.includes(u.id))
        );

        this.rightItem = undefined;
        this.matchedItems = undefined;

        this.spinnerService.show();
        this.lutService.mergeUniversitiesBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Universities merged successfully');
            this.lookupTable.clearMatchingState();
            this.loadUniversities();
          },
          error: (error) => {
            console.error('Error merging universities:', error);
            this.spinnerService.hide();
            this.universities.set(originalUniversities);
            this.rightItem = originalUniversities.find(u => u.id === bulkMergeRequest.rightId);
            this.matchedItems = originalUniversities.filter(u => bulkMergeRequest.leftIds.includes(u.id));
            this.toast.error(`<strong>Failed to merge Universities:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  onEditUniversity(university: University): void {
  }

  onDeleteUniversity(university: University): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(university.name)
      .subscribe(result => {
        if (result) {
          const originalUniversities = this.universities();

          this.universities.update(universities => universities.filter(u => u.id !== university.id));

          this.spinnerService.show();
          this.lutService.deleteUniversity(university.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('University deleted successfully');
              // Reload current page
              this.loadUniversities();
            },
            error: (error) => {
              console.error('Error deleting university:', error);
              this.spinnerService.hide();
              this.universities.set(originalUniversities);
              this.toast.error(`<strong>Failed to delete University:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: University, newName: string} | {item: University, newValue: any, columnKey: string}): void {
    const originalItem = { ...event.item };

    let updatedItem: University;
    if ('newName' in event) {
      updatedItem = { ...event.item, name: event.newName };
    } else {
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };

      // If updating countryId, also update countryName
      if (event.columnKey === 'countryId') {
        const countryName = event.newValue ? this.countries.find(c => c.id === event.newValue)?.name || null : null;
        updatedItem = { ...updatedItem, countryName: countryName };
      }
    }

    this.universities.update(universities =>
      universities.map(u => u.id === event.item.id ? updatedItem : u)
    );

    this.spinnerService.show();
    this.lutService.updateUniversity(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('University updated successfully');
        this.universities.update(universities =>
          universities.map(u => u.id === event.item.id ? response : u)
        );
      },
      error: (error) => {
        console.error('Error updating university:', error);
        this.spinnerService.hide();
        this.universities.update(universities =>
          universities.map(u => u.id === event.item.id ? originalItem : u)
        );
        this.toast.error(`<strong>Failed to update University:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    const tempId = crypto.randomUUID();
    const countryId = event['countryId'] || null;
    const countryName = countryId ? this.countries.find(c => c.id === countryId)?.name || null : null;

    const newUniversity: University = {
      id: tempId,
      name: event['name'],
      countryId: countryId,
      countryName: countryName,
      systemReserved: false
    };

    this.universities.update(universities => [newUniversity, ...universities]);

    this.spinnerService.show();
    this.lutService.createUniversity({
      name: event['name'],
      countryId: countryId,
      countryName: countryName,
      systemReserved: false
    }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('University created successfully');
        this.universities.update(universities =>
          universities.map(u => u.id === tempId ? response : u)
        );
      },
      error: (error) => {
        console.error('Error creating university:', error);
        this.spinnerService.hide();
        this.universities.update(universities =>
          universities.filter(u => u.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create University:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
